import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getUser, updateUser, deleteUser, verifyUserEmail, generateAccessCode } from '../../../api';
import Input from '../../../common/Input';
import { swalConfig, toastConfig } from '../../../config';
import useRouteStore from '../../../hooks/useRouteStore';
import './User.css';

const SpinnerIcon = () => (
  <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
  </svg>
);

const User = () => {
  const [user, setUser] = useState({});
  const [role, setRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const MySwal = withReactContent(Swal);
  const setRoute = useRouteStore((state) => state.setRoute);
  const navigate = useNavigate();
  const { id } = useParams();

  const roleOptions = [
    { value: 'admin', label: 'Administrador' },
    { value: 'donor', label: 'Donador' },
    { value: 'user', label: 'Usuario' },
  ];

  useEffect(() => {
    setRoute([
      { name: 'Inicio', url: '/administracion' },
      { name: 'Usuarios', url: '/administracion/usuarios' },
      { name: user.name ? `${user.name} ${user.lastname}` : 'Usuario', url: '/administracion/usuarios/' + id },
    ]);
  }, [user]);

  useEffect(() => {
    getUser(id).then((res) => {
      setUser(res.data);
      const userRole = roleOptions.find((r) => r.value === res.data.role);
      setRole(userRole);
    });
  }, []);

  const saveUser = async () => {
    setSaving(true);
    try {
      await updateUser(id, { ...user, role: role.value });
      toast.success('Usuario actualizado', toastConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar', toastConfig);
    } finally {
      setSaving(false);
    }
  };

  const verifyEmail = async () => {
    setVerifying(true);
    try {
      await verifyUserEmail(id);
      toast.success('Email verificado', toastConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al verificar', toastConfig);
    } finally {
      setVerifying(false);
    }
  };

  const handleGenerateAccessCode = async () => {
    setGeneratingCode(true);
    try {
      await generateAccessCode(id);
      toast.success('Código de acceso generado', toastConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al generar código', toastConfig);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleDeleteUser = () => {
    MySwal.fire({
      ...swalConfig['delete'],
      text: `El usuario ${user.name} ${user.lastname} será eliminado permanentemente`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeleting(true);
        try {
          await deleteUser(id);
          toast.success('Usuario eliminado', toastConfig);
          navigate('/administracion/usuarios');
        } catch (err) {
          toast.error(err.response?.data?.message || 'Error al eliminar', toastConfig);
          setDeleting(false);
        }
      }
    });
  };

  const anyLoading = saving || verifying || generatingCode || deleting;

  return (
    <div className='font-montserrat w-full max-h-full'>
      {Object.keys(user).length > 0 ? (
        <>
          {/* Title bar */}
          <div className='flex items-center justify-between gap-3 pb-1 mb-4 border-b-2'>
            <h1 className='font-semibold text-xl min-w-0 truncate'>
              Usuario: {user.name} {user.lastname}
            </h1>
            <button
              onClick={saveUser}
              disabled={anyLoading}
              className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded hover:bg-red-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shrink-0'
            >
              {saving && <SpinnerIcon />}
              Guardar
            </button>
          </div>

          {/* Form */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
            <Input
              id='name'
              value={user.name}
              onChange={(e) => setUser({ ...user, name: e.target.value })}
              label='Nombre'
              labelClassName='w-28'
            />
            <Input
              id='lastname'
              value={user.lastname}
              onChange={(e) => setUser({ ...user, lastname: e.target.value })}
              label='Apellido'
              labelClassName='w-28'
            />
            <Input
              id='email'
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              label='Correo'
              labelClassName='w-28'
            />
            <div className='flex items-center gap-2 font-montserrat text-lg'>
              <label htmlFor='role' className='font-semibold text-red-1 whitespace-nowrap w-28 shrink-0'>
                Rol
              </label>
              <Select
                id='role'
                options={roleOptions}
                value={role}
                onChange={(value) => setRole(value)}
                className='w-full text-base'
              />
            </div>
          </div>

          {/* Actions */}
          <div className='mt-6 border-t-2 pt-4'>
            <h2 className='text-lg font-semibold mb-3'>Acciones</h2>
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>

              {/* Verify email */}
              <div className='flex flex-col gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4'>
                <div className='flex items-center gap-2'>
                  <span className='flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 shrink-0'>
                    <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                    </svg>
                  </span>
                  <span className='font-semibold text-sm text-blue-800'>Verificar correo</span>
                </div>
                <p className='text-xs text-blue-600 leading-relaxed'>
                  Marca el correo del usuario como verificado manualmente.
                </p>
                <button
                  onClick={verifyEmail}
                  disabled={anyLoading}
                  className='mt-auto flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 py-1.5 rounded transition-colors'
                >
                  {verifying && <SpinnerIcon />}
                  Verificar
                </button>
              </div>

              {/* Generate access code */}
              <div className='flex flex-col gap-3 rounded-xl border border-green-100 bg-green-50 p-4'>
                <div className='flex items-center gap-2'>
                  <span className='flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 shrink-0'>
                    <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' />
                    </svg>
                  </span>
                  <span className='font-semibold text-sm text-green-800'>Código de acceso</span>
                </div>
                <p className='text-xs text-green-600 leading-relaxed'>
                  Genera un código temporal para que el usuario pueda acceder.
                </p>
                <button
                  onClick={handleGenerateAccessCode}
                  disabled={anyLoading}
                  className='mt-auto flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 py-1.5 rounded transition-colors'
                >
                  {generatingCode && <SpinnerIcon />}
                  Generar
                </button>
              </div>

              {/* Delete user */}
              <div className='flex flex-col gap-3 rounded-xl border border-red-100 bg-red-50 p-4'>
                <div className='flex items-center gap-2'>
                  <span className='flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600 shrink-0'>
                    <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                    </svg>
                  </span>
                  <span className='font-semibold text-sm text-red-800'>Eliminar usuario</span>
                </div>
                <p className='text-xs text-red-600 leading-relaxed'>
                  Elimina permanentemente este usuario y todos sus datos.
                </p>
                <button
                  onClick={handleDeleteUser}
                  disabled={anyLoading}
                  className='mt-auto flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 py-1.5 rounded transition-colors'
                >
                  {deleting && <SpinnerIcon />}
                  Eliminar
                </button>
              </div>

            </div>
          </div>
        </>
      ) : (
        <h1 className='font-semibold text-xl'>
          <span className='loader2'></span>
        </h1>
      )}
    </div>
  );
};

export default User;
